import React, { useState } from 'react';
import { Alert, Autocomplete, Chip, Divider, Grid, IconButton, MenuItem, TextField } from '@mui/material';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { LoadingButton } from '@mui/lab';
import { Delete } from '@mui/icons-material';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { AccountData, CatalogCategoryOut, CatalogItemOut, CatalogService, PartialLabelData } from '../api';
import TitledDrawer from '../common/ui/TitledDrawer';
import { useAuth } from '../session/InternalAuthProvider';
import { useLanguage } from '../common/GeneralUtils';
import { localizedLabel } from '../utils/maskUtils';
import { getCurrencySymbol } from '../utils/utils';
import ConfirmationDialog from '../common/ui/ConfimationDialog';
import { useApiResponseHandlerContext } from '../common/apiNotifications/useApiResponseHandlerContext';
import { ApiResponseHandlerType } from '../common/apiNotifications/ApiResponseHandlerContext';
import { ImageUploadWidget } from '../emailTemplates/templateEditor/components/bytescale/ImageUploadWidget';
import {
  buildItemPayload,
  CatalogItemDrawerType,
  CatalogItemFormValues,
  labelHasValue,
  suffixedLabel,
} from './catalogUtils';

type EditCatalogItemDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  type: CatalogItemDrawerType;
  // The source row for EDIT and CLONE alike; CLONE reads it for the initial values only and
  // still posts a brand new item.
  item?: CatalogItemOut;
  categories: CatalogCategoryOut[];
  existingTags: string[];
  defaultCategoryId?: string;
  onSaved: (item: CatalogItemOut) => void;
};

export const EditCatalogItemDrawer = ({
  isOpen,
  onClose,
  type,
  item,
  categories,
  existingTags,
  defaultCategoryId,
  onSaved,
}: EditCatalogItemDrawerProps) => {
  const { t } = useTranslation();
  // @ts-ignore
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const language = useLanguage();
  const { showResponseNotification } = useApiResponseHandlerContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [archiveConfirmIsOpen, setArchiveConfirmIsOpen] = useState(false);
  const languages = account.customer_facing_widgets_languages?.length
    ? account.customer_facing_widgets_languages
    : [language];
  const isEdit = type === CatalogItemDrawerType.EDIT;
  // Only the item being edited keeps an archived category; a clone has to land somewhere active.
  const isArchived = isEdit && !!item?.archived;
  const selectableCategories = categories
    .filter((category) => !category.archived || (isEdit && category.id === item?.category_id))
    .sort((first, second) =>
      localizedLabel(first.label, language).localeCompare(localizedLabel(second.label, language), language),
    );
  const selectableCategoryIds = new Set(selectableCategories.map((category) => category.id));

  const mandatoryField = t('calendar.edit_order.mandatoryField');
  const labelSchema = yup
    .object()
    .test('has-label', t('catalog.atLeastOneLanguage'), (value) => labelHasValue(value as PartialLabelData));
  const validationSchema = yup.object().shape({
    category_id: yup.string().required(mandatoryField),
    name: labelSchema,
    price: yup.number().typeError(mandatoryField).required(mandatoryField).min(0, t('calendar.edit_order.positive')),
  });

  const initialCategoryId = item?.category_id ?? defaultCategoryId ?? '';
  const initialValues: CatalogItemFormValues = {
    category_id: selectableCategoryIds.has(initialCategoryId) ? initialCategoryId : '',
    name:
      type === CatalogItemDrawerType.CLONE
        ? suffixedLabel(item?.name, t('catalog.copySuffix'))
        : { en: item?.name?.en ?? undefined, he: item?.name?.he ?? undefined },
    description: { en: item?.description?.en ?? undefined, he: item?.description?.he ?? undefined },
    photo_url: item?.photo_url ?? undefined,
    price: item?.price ?? '',
    tags: item?.tags ?? [],
  };

  const onSubmit = async (values: CatalogItemFormValues) => {
    try {
      setIsSubmitting(true);
      const payload = buildItemPayload(values);
      const savedItem =
        isEdit && item
          ? await CatalogService.updateCatalogItem({ ...payload, id: item.id })
          : await CatalogService.createCatalogItem(payload);
      onSaved(savedItem);
      onClose();
    } catch (error: any) {
      showResponseNotification(error?.body?.detail || t('api.error.default'), ApiResponseHandlerType.ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onArchiveConfirmClose = async (actionConfirmed: boolean) => {
    if (actionConfirmed && item) {
      try {
        const archivedItem = await CatalogService.archiveCatalogItem({ id: item.id });
        onSaved(archivedItem);
        setArchiveConfirmIsOpen(false);
        onClose();
        return;
      } catch (error: any) {
        showResponseNotification(error?.body?.detail || t('api.error.default'), ApiResponseHandlerType.ERROR);
      }
    }
    setArchiveConfirmIsOpen(false);
  };

  const titleByType: Record<CatalogItemDrawerType, string> = {
    [CatalogItemDrawerType.CREATE]: t('catalog.addItem'),
    [CatalogItemDrawerType.EDIT]: t('catalog.editItem'),
    [CatalogItemDrawerType.CLONE]: t('catalog.cloneItem'),
  };
  const title = titleByType[type];

  return (
    <TitledDrawer title={title} open={isOpen} onClose={onClose}>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {({ setFieldValue, values, errors, touched, handleChange, submitForm }) => (
          <Form style={{ height: '100%' }}>
            <Grid
              container
              flexDirection="column"
              flexWrap="nowrap"
              justifyContent="space-between"
              overflow="hidden"
              height="100%">
              <Grid
                gap={1.5}
                container
                flexDirection="column"
                flexWrap="nowrap"
                flex={1}
                minHeight={0}
                sx={{
                  overflowY: 'auto',
                  pt: { xs: '12px', md: '18px' },
                  pb: 2,
                  paddingInline: { xs: '12px', md: '24px' },
                }}>
                {isArchived && <Alert severity="warning">{t('catalog.archivedItemReadOnly')}</Alert>}
                <TextField
                  select
                  size="small"
                  name="category_id"
                  label={t('catalog.category')}
                  value={values.category_id}
                  onChange={handleChange}
                  disabled={isArchived}
                  error={touched.category_id && Boolean(errors.category_id)}
                  helperText={touched.category_id && errors.category_id}>
                  {selectableCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {localizedLabel(category.label, language)}
                    </MenuItem>
                  ))}
                </TextField>
                <Grid item container gap={2} flexWrap="nowrap">
                  {languages.map((formLanguage) => (
                    <TextField
                      key={formLanguage}
                      fullWidth
                      size="small"
                      name={`name.${formLanguage}`}
                      label={`${t('catalog.itemName')} (${t(`emailTemplates.${formLanguage}`)})`}
                      value={values.name[formLanguage] || ''}
                      onChange={handleChange}
                      disabled={isArchived}
                    />
                  ))}
                </Grid>
                {typeof errors.name === 'string' && touched.name && (
                  <Typography color="error" variant="caption">
                    {errors.name}
                  </Typography>
                )}
                <Grid item container gap={2} flexWrap="nowrap">
                  {languages.map((formLanguage) => (
                    <TextField
                      key={formLanguage}
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      name={`description.${formLanguage}`}
                      label={`${t('catalog.itemDescription')} (${t(`emailTemplates.${formLanguage}`)})`}
                      value={values.description[formLanguage] || ''}
                      onChange={handleChange}
                      disabled={isArchived}
                    />
                  ))}
                </Grid>
                <Grid item container flexDirection="column" gap={1}>
                  <Typography variant="subtitle2">{t('catalog.photo')}</Typography>
                  {values.photo_url ? (
                    <Grid container alignItems="flex-start" gap={1}>
                      <img
                        src={values.photo_url}
                        alt=""
                        style={{ maxWidth: '120px', maxHeight: '120px', borderRadius: '8px', objectFit: 'cover' }}
                      />
                      <IconButton disabled={isArchived} onClick={() => setFieldValue('photo_url', undefined)}>
                        <Delete />
                      </IconButton>
                    </Grid>
                  ) : (
                    !isArchived && (
                      <ImageUploadWidget onImageUpload={(files) => setFieldValue('photo_url', files[0]?.fileUrl)} />
                    )
                  )}
                </Grid>
                <Divider />
                <Grid item container flexDirection="column" gap={1}>
                  <Typography variant="subtitle2">{t('catalog.pricing')}</Typography>
                  <Grid item maxWidth="186px">
                    <TextField
                      size="small"
                      type="number"
                      name="price"
                      label={t('catalog.price')}
                      value={values.price}
                      onChange={handleChange}
                      disabled={isArchived}
                      error={touched.price && Boolean(errors.price)}
                      helperText={touched.price && (errors.price as string)}
                      InputProps={{ endAdornment: getCurrencySymbol(account), inputProps: { min: 0, dir: 'ltr' } }}
                    />
                  </Grid>
                </Grid>
                <Divider />
                <Autocomplete
                  multiple
                  freeSolo
                  size="small"
                  options={existingTags}
                  value={values.tags}
                  disabled={isArchived}
                  onChange={(_, newTags) => setFieldValue('tags', newTags)}
                  renderTags={(tags, getTagProps) =>
                    tags.map((tag, index) => <Chip label={tag} size="small" {...getTagProps({ index })} />)
                  }
                  renderInput={(params) => (
                    <TextField {...params} size="small" label={t('catalog.tags')} helperText={t('catalog.tagsHint')} />
                  )}
                />
              </Grid>
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  flexShrink: 0,
                  py: 1.5,
                  paddingInline: { xs: '12px', md: '24px' },
                  borderTop: '1px solid',
                  borderColor: 'mb.lineSoft',
                }}>
                <Grid item>
                  {isEdit && item && !isArchived && (
                    <Button color="error" sx={{ textTransform: 'none' }} onClick={() => setArchiveConfirmIsOpen(true)}>
                      {t('catalog.archive')}
                    </Button>
                  )}
                </Grid>
                <LoadingButton
                  loading={isSubmitting}
                  disabled={isArchived}
                  variant="contained"
                  sx={{ textTransform: 'none' }}
                  onClick={submitForm}>
                  {isEdit ? t('save_changes') : t('add')}
                </LoadingButton>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
      {archiveConfirmIsOpen && (
        <ConfirmationDialog
          isOpen={archiveConfirmIsOpen}
          onClose={onArchiveConfirmClose}
          title={t('catalog.archiveItemTitle')}
          content={t('catalog.archiveItemContent')}
          ctaText={t('catalog.archive')}
        />
      )}
    </TitledDrawer>
  );
};
